import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';

import { PreorderRequest } from '../models/cart.model';
import {
  CheckoutIntentResult,
  CheckoutReserveResult,
  CheckoutSessionState,
  PaymentIntentResult,
  PaymentProvider,
  PaymentResult,
  PrepurchaseIntentResult,
  PrepurchaseSessionPaymentState,
} from './payment-provider.interface';

/**
 * Implementación de relleno para Pasarela · 3. NO habla con ningún backend
 * ni con ONVO. Se usa en dev/tests mientras el `OnvoPaymentProvider` real y
 * los endpoints públicos llegan (Pasarela · 5).
 *
 * Importante: NUNCA emite `approved`. Devuelve siempre `pending`, lo que
 * mantiene honesta la semántica del paylink diferido (con el stub activo, el
 * método «online» sigue resolviéndose por enlace de pago por correo). Bajo
 * stub, además, ningún cliente real llega al paso de pago (ver D2 en el spec:
 * la navegación al paso solo ocurre con `providerId === 'onvo'`).
 *
 * Spec: docs/specs/checkout-payment-step-plan.md §3.6.
 */
@Injectable()
export class StubPaymentProvider implements PaymentProvider {
  readonly id = 'stub' as const;

  createIntent(preorderToken: string): Observable<PaymentIntentResult> {
    // amountColones 0 = placeholder: el stub no deriva montos (eso es
    // server-side) y, por D2, no enruta clientes reales. El `delay` simula
    // latencia de red para que el skeleton del paso sea observable en el
    // eyeball QA opcional (N1) y para espejar el contrato del spec (§3.6).
    return of<PaymentIntentResult>({
      providerIntentId: `stub-${preorderToken}`,
      status: 'pending',
      amountColones: 0,
    }).pipe(delay(300));
  }

  confirm(
    _preorderToken: string,
    _intentId: string,
    _paymentMethodId?: string,
  ): Observable<PaymentResult> {
    return of<PaymentResult>({
      status: 'pending',
      message: 'Te enviaremos un enlace de pago por correo.',
    }).pipe(delay(400));
  }

  getStatus(_preorderToken: string, _intentId: string): Observable<PaymentResult> {
    return of({ status: 'pending' });
  }

  // Flujo diferido: el stub NO enruta clientes reales aquí (online+stub usa el camino antiguo de
  // cart-to-preorder, ver checkout onSubmit/D2). Stubs mínimos para satisfacer el contrato/tests.
  createCheckoutIntent(
    cartId: number,
    _payload: PreorderRequest,
  ): Observable<CheckoutIntentResult> {
    return of<CheckoutIntentResult>({
      sessionToken: `stub-${cartId}`,
      providerIntentId: `stub-pi-${cartId}`,
      status: 'pending',
      amountColones: 0,
    }).pipe(delay(300));
  }

  // Puerta de reserva manual: el stub NO enruta clientes reales aquí. Stub mínimo para el contrato.
  reserveCheckout(
    cartId: number,
    _payload: PreorderRequest,
  ): Observable<CheckoutReserveResult> {
    return of<CheckoutReserveResult>({
      sessionToken: `stub-reserve-${cartId}`,
      reservationExpiresAt: null,
      reservationExpiresInSeconds: 900,
      amountColones: 0,
    }).pipe(delay(300));
  }

  getCheckoutSession(sessionToken: string): Observable<CheckoutSessionState> {
    return of<CheckoutSessionState>({
      sessionToken,
      consumed: false,
      providerIntentId: `stub-pi`,
      status: 'pending',
      amountColones: 0,
    });
  }

  completeCheckout(sessionToken: string): Observable<{ token: string }> {
    return of({ token: `stub-${sessionToken}` }).pipe(delay(400));
  }

  // Precompra con tarjeta (Bloque 3): el stub NO enruta clientes reales aquí. Stubs mínimos para
  // satisfacer el contrato/tests.
  createPrepurchaseIntent(sessionToken: string): Observable<PrepurchaseIntentResult> {
    return of<PrepurchaseIntentResult>({
      sessionToken,
      providerIntentId: `stub-pi-${sessionToken}`,
      status: 'pending',
      amountColones: 0,
    }).pipe(delay(300));
  }

  getPrepurchaseSession(sessionToken: string): Observable<PrepurchaseSessionPaymentState> {
    return of<PrepurchaseSessionPaymentState>({
      sessionToken,
      status: 'pending',
      consumed: false,
      providerIntentId: `stub-pi`,
      intentStatus: 'pending',
      amountColones: 0,
      expiresInSeconds: 900,
    });
  }

  completePrepurchase(_sessionToken: string): Observable<{ prepurchaseId: number }> {
    return of({ prepurchaseId: 0 }).pipe(delay(400));
  }
}
